import React, {useContext} from "react";
import {DeleteBookingRequest, IKensuCalendarEventProto, KensuCalendarEventProto, space} from "../../../../provider_api";
import {useProviderProfile} from "../../../../ProviderProfileProvider";
import {priceToString} from "../../../../util/PriceUtil";
import {PaymentStatusWidget} from "../../../../util/BookingUtil";
import {EditableStringField} from "../../../../components/Editable/EditableStringField";
import {EditableSingleSelect} from "../../../../components/Editable/EditableSingleSelect";
import {LinkWithParams} from "../../../../util/NavUtils";
import {RepeatStrategyEditor} from "./RepeatStrategyEditor";
import {EditableContext} from "../../../../components/Editable/EditableContext";
import IApiBookingProto = space.kenko.proto.IApiBookingProto;
import {useDeleteClassBookingRpc} from "../../../../util/RpcHooks/DeleteClassBookingRpc";
import {findClassTemplate} from "./CalendarUtil";

type Props = {
  officeOptions: { value: string, label: string }[]
  classBookings: IKensuCalendarEventProto[],
  onBookingDeleted: (bookingId: string) => void,
}

/**
 * This is kinda like the AppointmentEditor, but you need to be able to see the number of people
 * who have signed up for the class and other details.
 * 
 * Note that this looks at the LIVE profile for the list of price categories and practitioners
 * and stuff. We don't want drafts to be used since calendar events go live right away.
 *
 */
export const ClassEditor = (props: Props) => {

  const {businessProfile} = useProviderProfile();
  const deleteBookingRpc = useDeleteClassBookingRpc();

  const {workingCopy, mode} =
      useContext<EditableContext<KensuCalendarEventProto>>(EditableContext);

  const deleteBookingForClass = (sendProps: { class: IKensuCalendarEventProto }) => {

    if (!sendProps.class.classBooking) {
      return;
    }

    if (!window.confirm('Remove ' + sendProps.class.classBooking.booking?.details?.clientName + ' from this class? ' +
        'The class credit they used will be added back to their account.')) {
      return;
    }

    deleteBookingRpc.send({
      request: new DeleteBookingRequest({
        bookingId: sendProps.class.classBooking.booking?.bookingId,
      }),
      onSuccess: props.onBookingDeleted,
    })
  }

  /**
   * This is a switch based on new and old style classes
   * TODO: remove once non-templated classes are gone.
   */
  const getMaxAttendees = () => {
    if (workingCopy.class?.details?.classTemplateId) {
      return findClassTemplate(businessProfile, workingCopy.class?.details?.classTemplateId)?.maxAttendees;
    }
    
    return workingCopy.class?.details?.maxAttendees;
  }

  return <>
    <div className={'CalendarModalRow'}>

      <EditableSingleSelect<IKensuCalendarEventProto, string>
          label={'Class Type'}
          options={businessProfile?.studio?.classTemplates?.filter(
              template => !template.archived)
              .map(template => {
                return {label: template.name as string, value: template.classTemplateId as string}
              }) ?? []}
          getter={instance => instance.class?.details?.classTemplateId}
          setter={(instance, value) => new KensuCalendarEventProto({
            ...instance,
            class: {
              ...instance?.class,
              details: {
                ...instance?.class?.details,
                classTemplateId: value,

                /**
                 * This clears out some fields which are now set on the class template ID and
                 * can't be set by the client any longer.
                 * 
                 * TODO: remove once all old events are gone.
                 */
                className: undefined,
                paymentPolicy: undefined,
                maxAttendees: undefined,
              }
            }
          })}
          errorKey={'className'}
      />

      <EditableSingleSelect<IKensuCalendarEventProto, string>
          label={'Teacher'}
          getter={instance => instance.class?.details?.teacherPractitionerId}
          setter={(instance, value) => new KensuCalendarEventProto({
            ...instance,
            class: {
              ...instance.class,
              details: {
                ...instance.class?.details,
                teacherPractitionerId: value
              }
            }
          })}
          errorKey={'teacherPractitionerId'}
          options={businessProfile?.practitioners?.filter(p => !p.archived).map(p => {
            return {
              value: p.practitionerId as string,
              label: <>{p.firstName} {p.lastName}</>
            }
          }) ?? []}
      />

    </div>
    <div className={'CalendarModalRow'}>

      <EditableSingleSelect<IKensuCalendarEventProto, string>
          label={'Location'}
          getter={instance => instance.class?.details?.locationId}
          setter={(instance, value) => new KensuCalendarEventProto({
            ...instance,
            class: {
              ...instance.class,
              details: {
                ...instance.class?.details,
                locationId: value
              }
            }
          })}
          errorKey={'location'}
          options={props.officeOptions}
      />

    </div>

    
    {/* This shows signups, but only if editing */}
    {mode == 'update' && <div className={'CalendarModalRow'}>

      <div className={'VerticalFlex'} style={{marginTop: 5, marginBottom: 5, gap: 5}}>
        <b>{props.classBookings?.length || 0}/{getMaxAttendees()} client(s) attending</b>
        {props.classBookings?.map(booking => {
          return <div className={'CalendarAttendeeRow'}>
            <LinkWithParams to={`/clients/${booking.classBooking?.booking?.userId}`}>
              <div
                  style={{textDecoration: 'underline'}}>{booking.classBooking?.booking?.details?.clientName || 'Empty name'}</div>
            </LinkWithParams>
            <button className={'RemoveFromClassButton'}
                    onClick={() => deleteBookingForClass({class: booking})}>remove
            </button>
            <PaymentStatusWidget booking={booking.classBooking?.booking as IApiBookingProto}/>
          </div>
        })}
        {deleteBookingRpc.rpcState.state == 'error' && <div className={'ErrorText'}>Error deleting person from class. {deleteBookingRpc.rpcState.message}</div>}
      </div>
    </div>}


    <RepeatStrategyEditor<IKensuCalendarEventProto>
        label={'Recurring'}
        immutable={mode == 'update'}
        errorKey={'repeatStrategy'}
        startTimestamp={workingCopy.startTimestamp as number}
        getter={instance => instance.class?.repeatStrategy}
        setter={(instance, value) => new KensuCalendarEventProto({
          ...instance,
          class: {
            ...instance.class,
            repeatStrategy: value
          }
        })}
    />
  </>
}